"use client"
import { isEmpty, noop } from "lodash";
import { FC, useState, useCallback, ChangeEvent, FormEvent, KeyboardEvent, MouseEvent } from "react";
import { RotatingLines } from "react-loader-spinner";
import cn from "classnames";
import { Textfield } from "./Textfield";
import { Button } from "./Button";
import { EditFieldProps } from ".";
import Styles from "./forms.module.scss";

export const EditField: FC<EditFieldProps> = ({ value: originalValue = "", onSubmit = noop, onAbort = noop, loading = false, ...props }) => {
  const [newValue, setNewValue] = useState<string>(originalValue);
  const [editMode, setEditMode] = useState<boolean>(isEmpty(originalValue));

  const onChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    const target = e.target as HTMLInputElement;
    setNewValue(target.value);
  }, [setNewValue]);

  const save = useCallback(() => {
    onSubmit(newValue);
    setEditMode(false);
  }, [newValue,onSubmit]);

  const abort = useCallback(() => {
    if (newValue.length && !originalValue.length) {
      save();
    } else if (newValue.length && originalValue.length) {
      setNewValue(originalValue);
    } else if (!newValue.length && !originalValue.length) {
      onAbort();
    }
    setEditMode(false);
  }, [save, newValue.length, onAbort, originalValue]);

  const _onSubmit = useCallback((e: FormEvent) => {
    e.preventDefault();
    save();
  }, [save]);

  const onKeyUp = useCallback(({ key }: KeyboardEvent<HTMLFormElement>) => {
    if (key === "Escape") {
      abort();
    }
  }, [abort]);

  const onSetEditMode = useCallback((mode: boolean) => (e: MouseEvent<HTMLButtonElement | HTMLDivElement>) => {
    e.stopPropagation();
    e.preventDefault();
    if (mode) {
      setEditMode(true);
    } else {
      abort();
    }
  }, [abort]);

  if (editMode) {
    return (
      <form
        onKeyUp={onKeyUp}
        onSubmit={_onSubmit}
        className={cn(Styles.editField, { editMode })}
      >
        <Textfield
          onChange={onChange}
          value={newValue}
          {...props}
        />
        <Button
          onClick={onSetEditMode(false)}
          customClass={Styles.editCloseIcon}
          value="&#x24e7;"
          type="button"
        />
      </form>
    );
  }
  return (
    <div onClick={onSetEditMode(true)} className={cn(Styles.editField, { editMode })}>
      {originalValue}
      {loading
        ? <div className={Styles.spinner}>
            <RotatingLines
              width="36"
              strokeColor="black"
              strokeWidth="3"
              animationDuration="0.25s"
            />
          </div>
        : <Button
            onClick={onSetEditMode(true)}
            customClass={Styles.editCloseIcon}
            value="&#x270e;"
            type="button"
          />
      }
    </div>
  );
};

export default EditField;
